import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { FileSearch, Search } from 'lucide-react';
import { Modal, Spinner } from '@components/base/feedback';
import { TextField } from '@components/base/inputs';
import { List } from '@components/base/data';
import { Icon, Text, Tooltip } from '@components/base/display';
import { HStack, Panel, VStack } from '@components/base/layout';
import { claimSearchService, type ClaimSearchResult } from '../api/claimSearchService';
import { useClaimFlowAuthStore } from '../auth/store';
import { useClaimSearchStore } from './claimSearchStore';
import { buildClaimSearchResultUrl, CLAIM_SEARCH_CONFIG } from './claimSearchConfig';

const TEXT = CLAIM_SEARCH_CONFIG.text;

/**
 * The session id of the current view, resolved like `TaskDetailPage` does:
 * route state (dashboard → task navigation) → `?ses=` → the spoofed session of
 * the auth store.
 */
function useCurrentSessionId(): string | null {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const storedSessionId = useClaimFlowAuthStore((s) => s.spoofedSessionId);
  const state = location.state as { ses?: string } | null;
  return (
    state?.ses ??
    searchParams.get('ses') ??
    (storedSessionId == null ? null : String(storedSessionId))
  );
}

// ---------------------------------------------------------------------------
// Debounced search
// ---------------------------------------------------------------------------

type ClaimSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; query: string; results: ClaimSearchResult[] }
  | { status: 'error'; query: string };

type SettledSearch = Extract<ClaimSearchState, { status: 'success' | 'error' }>;

/**
 * Debounced, abortable search on the trimmed `query`. A new query clears the
 * pending timer and aborts the in-flight request, and a response is only kept
 * if its query is still the current one — a stale response never wins.
 */
function useClaimSearch(query: string): ClaimSearchState {
  const [settled, setSettled] = useState<SettledSearch | null>(null);
  const isSearchable = query.length >= CLAIM_SEARCH_CONFIG.minQueryLength;

  useEffect(() => {
    if (!isSearchable) return;
    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(() => {
      void claimSearchService.searchClaims(query, controller.signal).then(
        (results) => {
          if (active) setSettled({ status: 'success', query, results });
        },
        () => {
          // An aborted request also lands here — `active` is already false.
          if (active) setSettled({ status: 'error', query });
        },
      );
    }, CLAIM_SEARCH_CONFIG.debounceMs);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, isSearchable]);

  if (!isSearchable) return { status: 'idle' };
  if (settled?.query !== query) return { status: 'loading' };
  return settled;
}

// ---------------------------------------------------------------------------
// Result rows
// ---------------------------------------------------------------------------

const NO_RESULTS: ClaimSearchResult[] = [];

function ClaimSearchResultLines({ result }: Readonly<{ result: ClaimSearchResult }>) {
  return (
    <VStack as="span" gap={0}>
      <Text as="span" truncate>
        <Text as="span" weight="semibold">
          {result.ncbtSin}
        </Text>
        {result.ncieSin && (
          <Text as="span" color="muted">
            {TEXT.referenceSeparator}
            {result.ncieSin}
          </Text>
        )}
      </Text>
      <Text as="span" size="sm" color="muted" truncate>
        {result.nom2Per}
      </Text>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Modal content — unmounted with the modal, so every opening starts afresh.
// ---------------------------------------------------------------------------

interface ClaimSearchPanelProps {
  inputRef: RefObject<HTMLInputElement | null>;
  onOpenResult: (result: ClaimSearchResult) => void;
}

function ClaimSearchPanel({ inputRef, onOpenResult }: Readonly<ClaimSearchPanelProps>) {
  const [query, setQuery] = useState('');
  const search = useClaimSearch(query.trim());
  const results = search.status === 'success' ? search.results : NO_RESULTS;
  const resultsQuery = search.status === 'success' ? search.query : null;

  // Only rows with a claim id can be opened, hence selected.
  const enabledIndexes = results.flatMap((result, index) =>
    result.idSin == null ? [] : [index],
  );

  // Keyboard selection, tied to the result set it was made on: new results
  // start on their first openable row.
  const [selection, setSelection] = useState<{ query: string; index: number } | null>(null);
  const activeIndex =
    selection !== null &&
    selection.query === resultsQuery &&
    enabledIndexes.includes(selection.index)
      ? selection.index
      : (enabledIndexes[0] ?? -1);

  // Keep the keyboard-selected row visible in the scrollable list.
  const rowRefs = useRef(new Map<number, HTMLLIElement>());
  useEffect(() => {
    rowRefs.current.get(activeIndex)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, results]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (resultsQuery === null || enabledIndexes.length === 0) return;
      const position = enabledIndexes.indexOf(activeIndex);
      const nextPosition =
        event.key === 'ArrowDown'
          ? Math.min(position + 1, enabledIndexes.length - 1)
          : Math.max(position - 1, 0);
      const nextIndex = enabledIndexes[nextPosition];
      if (nextIndex !== undefined) setSelection({ query: resultsQuery, index: nextIndex });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const result = results[activeIndex];
      if (result) onOpenResult(result);
    }
  };

  const setRowRef = (index: number) => (node: HTMLLIElement | null) => {
    if (node) rowRefs.current.set(index, node);
    else rowRefs.current.delete(index);
  };

  return (
    <VStack gap={4}>
      <TextField
        ref={inputRef}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={TEXT.placeholder}
        aria-label={TEXT.inputLabel}
        autoComplete="off"
        leftElement={<Icon icon={Search} size="sm" color="muted" />}
      />
      {search.status === 'idle' && (
        <Text size="sm" color="muted">
          {TEXT.hint}
        </Text>
      )}
      {search.status === 'loading' && (
        <HStack justify="center" className="py-6">
          <Spinner size="md" />
        </HStack>
      )}
      {search.status === 'error' && (
        <Text size="sm" color="error">
          {TEXT.error}
        </Text>
      )}
      {search.status === 'success' && results.length === 0 && (
        <Text size="sm" color="muted">
          {TEXT.empty}
        </Text>
      )}
      {results.length > 0 && (
        <Panel variant="outlined" padding="none" className="max-h-80 overflow-y-auto">
          <List dividers>
            {results.map((result, index) =>
              result.idSin == null ? (
                <List.Item
                  key={`${String(index)}-${result.ncbtSin}`}
                  ref={setRowRef(index)}
                  disabled
                >
                  <Tooltip content={TEXT.missingIdSin}>
                    <span className="block">
                      <ClaimSearchResultLines result={result} />
                    </span>
                  </Tooltip>
                </List.Item>
              ) : (
                <List.Item
                  key={`${String(index)}-${result.ncbtSin}`}
                  ref={setRowRef(index)}
                  selected={index === activeIndex}
                  onClick={() => {
                    onOpenResult(result);
                  }}
                >
                  <ClaimSearchResultLines result={result} />
                </List.Item>
              ),
            )}
          </List>
        </Panel>
      )}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

/**
 * "Rechercher un dossier" modal (mahhp 22861). Mounted once in
 * `ClaimFlowLayout` (dashboard + claim screen); opened by the header trigger
 * through {@link useClaimSearchStore}. Picking a result opens the claim — the
 * task-less claim screen — in a new tab, then closes the modal.
 */
export function ClaimSearchModal() {
  const isOpen = useClaimSearchStore((s) => s.isOpen);
  const close = useClaimSearchStore((s) => s.close);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const ses = useCurrentSessionId();
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input once the modal is open. This effect runs after the
  // Modal's own (child) effect, which focuses its first focusable element —
  // the header close button.
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const handleOpenResult = useCallback(
    (result: ClaimSearchResult) => {
      if (result.idSin == null || !tenantSlug) return;
      window.open(
        buildClaimSearchResultUrl({
          origin: window.location.origin,
          tenantSlug,
          idSin: result.idSin,
          ses,
        }),
        '_blank',
        'noopener',
      );
      close();
    },
    [tenantSlug, ses, close],
  );

  return (
    <Modal open={isOpen} onClose={close} size="md" aria-labelledby={titleId}>
      <Modal.Header>
        <HStack gap={2} align="center">
          <Icon icon={FileSearch} size="md" color="primary" />
          <Text as="h2" id={titleId} size="lg" weight="semibold">
            {TEXT.title}
          </Text>
        </HStack>
      </Modal.Header>
      <Modal.Body>
        <ClaimSearchPanel inputRef={inputRef} onOpenResult={handleOpenResult} />
      </Modal.Body>
    </Modal>
  );
}
